/**
 * Minimal session-scoped cache primitive for read-mostly API responses.
 *
 * Why this exists: a handful of API responses (form definitions, soon
 * application metadata) change very rarely within a single session but are
 * re-fetched on every navigation back to the form view. This module gives
 * callers a one-liner to cache those responses without pulling in a full
 * data-fetching library (React Query / SWR) before we have evidence that
 * the full structural commitment is worth it.
 *
 * Lifetime tiers:
 *   - In-memory `Map` — survives soft navigations within the same tab,
 *     cleared on tab reload.
 *   - sessionStorage (when `persist: true`) — additionally survives soft
 *     reloads within the same tab. Dies when the tab closes, which is the
 *     right freshness boundary for "form definitions for the session".
 *
 * Not used for: anything that changes per save (answers, readiness),
 * anything sensitive enough to leak across users (auth profile),
 * anything that should sync between tabs. localStorage is intentionally
 * NOT used so a logged-out tab doesn't leave stale data for the next user
 * on the same machine.
 *
 * Invalidation:
 *   - `invalidateSessionCache()` — wipe everything (call on logout).
 *   - `invalidateSessionCache("form:")` — wipe a prefix (e.g. after an
 *     admin form edit, if/when we add an in-app signal for it).
 *
 * Errors:
 *   - Fetcher errors are NOT cached. The next call will retry the network.
 *   - JSON / storage failures from sessionStorage are silently ignored —
 *     we fall back to network on the next call, which is the safe default.
 */

interface CacheEntry<T> {
  v: T;
  expiresAt: number;
}

const STORAGE_PREFIX = "lgv:cache:";
const memCache = new Map<string, CacheEntry<unknown>>();

export interface SessionCacheOptions {
  /** Time-to-live in milliseconds. Defaults to 30 minutes. */
  ttlMs?: number;
  /**
   * When true, write through to sessionStorage so soft reloads of the same
   * tab can serve from cache. Defaults to false.
   */
  persist?: boolean;
}

const DEFAULT_TTL_MS = 30 * 60 * 1000;

function readFromSessionStorage<T>(key: string, now: number): CacheEntry<T> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.expiresAt !== "number") return null;
    if (parsed.expiresAt <= now) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeToSessionStorage<T>(key: string, entry: CacheEntry<T>): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exhausted / unavailable — fine, in-memory still hits.
  }
}

/**
 * Cache a fetcher's result. The fetcher is called at most once per TTL
 * window for the given key.
 */
export async function withSessionCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts: SessionCacheOptions = {},
): Promise<T> {
  const now = Date.now();
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;

  // 1. In-memory tier.
  const memHit = memCache.get(key) as CacheEntry<T> | undefined;
  if (memHit && memHit.expiresAt > now) return memHit.v;

  // 2. sessionStorage tier (opt-in).
  if (opts.persist) {
    const sessionHit = readFromSessionStorage<T>(key, now);
    if (sessionHit) {
      memCache.set(key, sessionHit);
      return sessionHit.v;
    }
  }

  // 3. Network. Successful fetches are written through to both tiers.
  const value = await fetcher();
  const entry: CacheEntry<T> = { v: value, expiresAt: now + ttl };
  memCache.set(key, entry);
  if (opts.persist) writeToSessionStorage(key, entry);
  return value;
}

/**
 * Drop cache entries.
 *
 * @param prefix  When provided, only keys starting with this prefix are
 *                cleared (both tiers). When omitted, EVERYTHING is wiped —
 *                use this from the logout flow.
 */
export function invalidateSessionCache(prefix?: string): void {
  if (!prefix) {
    memCache.clear();
    try {
      const toDelete: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) toDelete.push(k);
      }
      toDelete.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // sessionStorage unavailable; in-memory clear is still effective.
    }
    return;
  }

  for (const k of Array.from(memCache.keys())) {
    if (k.startsWith(prefix)) memCache.delete(k);
  }
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX + prefix)) toDelete.push(k);
    }
    toDelete.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // Ignore.
  }
}
